import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useCookieConsent, CookiePreferences } from '@/hooks/useCookieConsent';

export function CookieBanner() {
  const {
    showBanner,
    showSettings,
    preferences,
    acceptAll,
    acceptNecessary,
    saveCustomPreferences,
    openSettings,
    closeSettings,
  } = useCookieConsent();

  const [customPrefs, setCustomPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
  });

  if (!showBanner && !showSettings) return null;

  const handleSaveCustom = () => {
    saveCustomPreferences(customPrefs);
  };

  // Settings panel
  if (showSettings) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={closeSettings}
        />
        
        {/* Settings Modal */}
        <div className="relative w-full max-w-lg bg-card border border-border rounded-xl shadow-xl animate-scale-in">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Cookie-instellingen
            </h2>
            <Button variant="ghost" size="icon" onClick={closeSettings}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              Beheer uw cookie-voorkeuren. Noodzakelijke cookies zijn altijd actief voor de werking van de website.
            </p>
            
            {/* Necessary Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex-1">
                <Label className="font-medium">Noodzakelijke cookies</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Essentieel voor de werking van de website. Kunnen niet worden uitgeschakeld.
                </p>
              </div>
              <Switch checked={true} disabled className="ml-4" />
            </div>
            
            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex-1">
                <Label className="font-medium">Analytische cookies</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Helpen ons te begrijpen hoe bezoekers de website gebruiken.
                </p>
              </div>
              <Switch 
                checked={customPrefs.analytics} 
                onCheckedChange={(checked) => setCustomPrefs(prev => ({ ...prev, analytics: checked }))}
                className="ml-4"
              />
            </div>
            
            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex-1">
                <Label className="font-medium">Marketing cookies</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Worden gebruikt voor gepersonaliseerde advertenties en remarketing.
                </p>
              </div>
              <Switch 
                checked={customPrefs.marketing} 
                onCheckedChange={(checked) => setCustomPrefs(prev => ({ ...prev, marketing: checked }))}
                className="ml-4"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 p-4 border-t border-border">
            <Button variant="outline" onClick={acceptNecessary} className="flex-1">
              Alleen noodzakelijk
            </Button>
            <Button onClick={handleSaveCustom} className="flex-1">
              <Check className="h-4 w-4 mr-2" />
              Voorkeuren opslaan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main Banner
  return (
    <div 
      className={cn(
        "fixed bottom-0 left-0 right-0 z-[100] p-4 animate-fade-in",
        "sm:p-6"
      )}
    >
      <div className="container mx-auto max-w-4xl">
        <div className="bg-card border border-border rounded-xl shadow-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
            {/* Icon & Text */}
            <div className="flex gap-3 flex-1">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">
                  Wij gebruiken cookies
                </h3>
                <p className="text-sm text-muted-foreground">
                  We gebruiken cookies om uw ervaring te verbeteren en onze website te analyseren. 
                  Door op "Accepteren" te klikken, gaat u akkoord met ons{' '}
                  <Link to="/cookies" className="text-primary underline hover:text-primary/80">
                    cookiebeleid
                  </Link>.
                </p>
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={openSettings}
                className="order-3 sm:order-1"
              >
                Instellingen
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={acceptNecessary}
                className="order-2"
              >
                Alleen noodzakelijk
              </Button>
              <Button 
                size="sm" 
                onClick={acceptAll}
                className="order-1 sm:order-3"
              >
                Accepteren
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
